import {
  type BlockCollection,
  type CollectionAccess,
  type CustomBlockDefinition,
  type LibraryBlockDefinition,
  type TexturePixels,
  type VoxelMaterial,
  customBlockId,
  libraryBlockId,
  packVoxelMaterial,
  libraryBlockType,
  STANDARD_BLOCK_TYPES as STANDARD_BLOCK_COUNT,
} from '@shared/protocol';
import { blockTypeById } from '../world/blocks';
import { createBlockIcon } from './BlockIcon';
import {
  InventoryPanel,
  allowDrop,
  readDragPayload,
  setBlockDragImage,
  setDragPayload,
  type InventoryHost,
} from './InventoryPanel';

export interface HotbarOptions {
  initialTypes: number[];
  customBlocks: CustomBlockDefinition[];
  libraryBlocks: LibraryBlockDefinition[];
  collections: BlockCollection[];
  canvas: HTMLElement;
  onChange(types: number[]): void;
  createCollection(
    name: string,
    description: string,
    access: CollectionAccess,
  ): Promise<BlockCollection>;
  updateCollection(
    id: number,
    name: string,
    description: string,
    access: CollectionAccess,
  ): Promise<BlockCollection>;
  deleteCollection(
    id: number,
    mode: 'keep-blocks' | 'delete-blocks',
  ): Promise<void>;
  createSolidBlock(
    name: string,
    color: string,
    opacity: number,
    collision?: CustomBlockDefinition['collision'],
    glowIntensity?: number | null,
  ): Promise<LibraryBlockDefinition>;
  createTexturedBlock(
    name: string,
    color: string,
    pixels: TexturePixels,
    noise?: Extract<LibraryBlockDefinition, { kind: 'textured' }>['noise'],
    collision?: CustomBlockDefinition['collision'],
  ): Promise<LibraryBlockDefinition>;
  createCustomBlock(
    name: string,
    gridSize: number,
    voxels: Array<[number, VoxelMaterial]>,
    noise?: CustomBlockDefinition['noise'],
    collision?: CustomBlockDefinition['collision'],
  ): Promise<CustomBlockDefinition>;
  updateSolidBlock(
    id: number,
    patch: {
      name: string;
      color?: string;
      opacity?: number;
      glowIntensity?: number | null;
      collectionId?: number | null;
      collision?: CustomBlockDefinition['collision'];
      editViewId?: number;
    },
  ): Promise<LibraryBlockDefinition>;
  updateTexturedBlock(
    id: number,
    name: string,
    color: string,
    pixels: TexturePixels,
    collectionId?: number | null,
    noise?: Extract<LibraryBlockDefinition, { kind: 'textured' }>['noise'],
    collision?: CustomBlockDefinition['collision'],
    editViewId?: number,
  ): Promise<LibraryBlockDefinition>;
  deleteLibraryBlock(id: number): Promise<void>;
  patchCustomBlock(
    id: number,
    patch: {
      name?: string;
      collectionId?: number | null;
      noise?: CustomBlockDefinition['noise'];
      collision?: CustomBlockDefinition['collision'];
      editViewId?: number;
    },
  ): Promise<CustomBlockDefinition>;
  updateCustomBlock(
    id: number,
    name: string,
    gridSize: number,
    voxels: Array<[number, VoxelMaterial]>,
    noise?: CustomBlockDefinition['noise'],
    collision?: CustomBlockDefinition['collision'],
    editViewId?: number,
  ): Promise<CustomBlockDefinition>;
  deleteCustomBlock(id: number): Promise<void>;
  createCustomBlockView(
    id: number,
    name?: string,
    sourceViewId?: number,
  ): Promise<CustomBlockDefinition>;
  activateCustomBlockView(id: number, viewId: number): Promise<CustomBlockDefinition>;
  renameCustomBlockView(
    id: number,
    viewId: number,
    name: string,
  ): Promise<CustomBlockDefinition>;
  deleteCustomBlockView(id: number, viewId: number): Promise<CustomBlockDefinition>;
  createLibraryBlockView(
    id: number,
    name?: string,
    sourceViewId?: number,
  ): Promise<LibraryBlockDefinition>;
  activateLibraryBlockView(id: number, viewId: number): Promise<LibraryBlockDefinition>;
  renameLibraryBlockView(
    id: number,
    viewId: number,
    name: string,
  ): Promise<LibraryBlockDefinition>;
  deleteLibraryBlockView(id: number, viewId: number): Promise<LibraryBlockDefinition>;
  updateCustomBlockAnimation(
    id: number,
    animation: CustomBlockDefinition['animation'],
  ): Promise<CustomBlockDefinition>;
  updateLibraryBlockAnimation(
    id: number,
    animation: LibraryBlockDefinition['animation'],
  ): Promise<LibraryBlockDefinition>;
}

/** Full draggable inventory plus a persistent ten-slot quick-access loadout. */
export class Hotbar {
  private selected = 0;
  private readonly slots: HTMLElement[] = [];
  private readonly customBlocks = new Map<number, CustomBlockDefinition>();
  private readonly libraryBlocks = new Map<number, LibraryBlockDefinition>();
  private readonly collections = new Map<number, BlockCollection>();
  private readonly quickRoot: HTMLElement;
  private readonly toggleButton: HTMLButtonElement;
  private readonly inventory: InventoryPanel;
  private types: number[];
  private gameVisible = false;

  constructor(
    private readonly root: HTMLElement,
    private readonly inventoryRoot: HTMLElement,
    private readonly options: HotbarOptions,
  ) {
    this.quickRoot = root.parentElement!;
    this.toggleButton = document.getElementById('inventory-toggle') as HTMLButtonElement;
    this.types = options.initialTypes.length === 10
      ? [...options.initialTypes]
      : Array.from({ length: 10 }, (_, index) => index === 0 ? 1 : 0);
    for (const definition of options.customBlocks) {
      this.customBlocks.set(definition.id, definition);
    }
    for (const definition of options.libraryBlocks) {
      this.libraryBlocks.set(definition.id, definition);
    }
    for (const collection of options.collections) {
      this.collections.set(collection.id, collection);
    }
    this.inventory = new InventoryPanel(this.inventoryRoot, this.createInventoryHost());
    this.inventoryRoot.addEventListener('inventory-close', ((event: CustomEvent<{ relock: boolean }>) => {
      this.closeInventory(event.detail.relock);
    }) as EventListener);
    this.bindQuickSlotRemoveDrop();
    this.bindDragVisuals();

    this.renderQuickAccess();
    this.inventory.render();
    this.toggleButton.onclick = (event) => {
      event.stopPropagation();
      this.toggleInventory();
    };

    document.addEventListener('keydown', (event) => {
      const inventoryOpen = this.isInventoryOpen;
      if (inventoryOpen) {
        // No in-game shortcuts while inventory is open; Escape / I still close it.
        // Ignore I while typing in search/name fields.
        if (event.code === 'Escape' && !event.repeat) {
          event.preventDefault();
          this.closeInventory(true);
          return;
        }
        if (event.code === 'KeyI' && !event.repeat && !isEditableTarget(event.target)) {
          event.preventDefault();
          this.closeInventory(true);
        }
        return;
      }
      // Dev panel owns Escape / F3; don't open inventory over it.
      if (document.getElementById('dev-popup')?.classList.contains('visible')) return;
      if (event.code === 'KeyI' && !event.repeat) {
        if (document.pointerLockElement === this.options.canvas) {
          event.preventDefault();
          this.toggleInventory();
        }
        return;
      }
      if (document.pointerLockElement !== this.options.canvas) return;
      const match = /^Digit(\d)$/.exec(event.code);
      if (!match) return;
      const digit = Number(match[1]);
      this.select(digit === 0 ? 9 : digit - 1);
    });
    this.select(0);
  }

  get selectedBlockId(): number {
    return this.types[this.selected];
  }

  get selectedStandardVoxelType(): number | null {
    const type = this.selectedBlockId;
    return type >= 1 && type <= STANDARD_BLOCK_COUNT ? type : null;
  }

  get selectedCustomBlock(): CustomBlockDefinition | null {
    const id = customBlockId(this.selectedBlockId);
    return id === null ? null : this.customBlocks.get(id) ?? null;
  }

  get selectedLibraryBlock(): LibraryBlockDefinition | null {
    const id = libraryBlockId(this.selectedBlockId);
    return id === null ? null : this.libraryBlocks.get(id) ?? null;
  }

  get selectedSolidColor(): string | null {
    const library = this.selectedLibraryBlock;
    if (library?.kind === 'solid') {
      return packVoxelMaterial(library.color, library.opacity, library.glowIntensity);
    }
    const type = this.selectedStandardVoxelType;
    return type === null ? null : blockTypeById(type).css;
  }

  get isInventoryOpen(): boolean {
    return this.inventoryRoot.classList.contains('visible');
  }

  /** Saved blocks enter inventory only; users explicitly drag them to quick access. */
  addCustomBlock(definition: CustomBlockDefinition): void {
    this.customBlocks.set(definition.id, definition);
    if (this.isInventoryOpen) this.inventory.render();
  }

  updateCustomBlock(definition: CustomBlockDefinition): void {
    this.customBlocks.set(definition.id, definition);
    this.renderQuickAccess();
    if (this.isInventoryOpen) this.inventory.render();
    this.select(this.selected);
  }

  addLibraryBlock(definition: LibraryBlockDefinition): void {
    this.libraryBlocks.set(definition.id, definition);
    if (this.isInventoryOpen) this.inventory.render();
  }

  updateLibraryBlock(definition: LibraryBlockDefinition): void {
    this.libraryBlocks.set(definition.id, definition);
    this.renderQuickAccess();
    if (this.isInventoryOpen) this.inventory.render();
    this.select(this.selected);
  }

  setVisible(visible: boolean): void {
    this.gameVisible = visible;
    this.quickRoot.classList.toggle('visible', visible || this.isInventoryOpen);
  }

  private removeLocalLibrary(id: number): void {
    this.libraryBlocks.delete(id);
    const type = libraryBlockType(id);
    let changed = false;
    this.types = this.types.map((value) => {
      if (value !== type) return value;
      changed = true;
      return 0;
    });
    if (changed) this.options.onChange([...this.types]);
  }

  private removeLocalCustom(id: number): void {
    this.customBlocks.delete(id);
    let changed = false;
    this.types = this.types.map((value) => {
      if (customBlockId(value) !== id) return value;
      changed = true;
      return 0;
    });
    if (changed) this.options.onChange([...this.types]);
  }

  private createInventoryHost(): InventoryHost {
    const hotbar = this;
    return {
      customBlocks: this.customBlocks,
      libraryBlocks: this.libraryBlocks,
      collections: this.collections,
      get types() { return hotbar.types; },
      get selectedSlot() { return hotbar.selected; },
      commitLoadout: (types, selectedSlot) => {
        hotbar.types = types;
        hotbar.commitLoadout(selectedSlot);
      },
      createCollection: async (name, description, access) => {
        const collection = await hotbar.options.createCollection(name, description, access);
        hotbar.collections.set(collection.id, collection);
        return collection;
      },
      updateCollection: async (id, name, description, access) => {
        const collection = await hotbar.options.updateCollection(id, name, description, access);
        hotbar.collections.set(collection.id, collection);
        return collection;
      },
      deleteCollection: async (id, mode) => {
        await hotbar.options.deleteCollection(id, mode);
        hotbar.collections.delete(id);
        if (mode === 'delete-blocks') {
          for (const [blockId, block] of [...hotbar.libraryBlocks]) {
            if (block.collectionId === id) hotbar.removeLocalLibrary(blockId);
          }
          for (const [blockId, block] of [...hotbar.customBlocks]) {
            if (block.collectionId === id) hotbar.removeLocalCustom(blockId);
          }
        } else {
          for (const block of hotbar.libraryBlocks.values()) {
            if (block.collectionId === id) block.collectionId = null;
          }
          for (const block of hotbar.customBlocks.values()) {
            if (block.collectionId === id) block.collectionId = null;
          }
        }
        hotbar.renderQuickAccess();
        hotbar.select(hotbar.selected);
      },
      createSolidBlock: async (name, color, opacity, collision, glowIntensity) => {
        const definition = await hotbar.options.createSolidBlock(
          name, color, opacity, collision, glowIntensity,
        );
        hotbar.libraryBlocks.set(definition.id, definition);
        return definition;
      },
      createCustomBlock: async (name, gridSize, voxels, noise, collision) => {
        const definition = await hotbar.options.createCustomBlock(
          name, gridSize, voxels, noise, collision,
        );
        hotbar.customBlocks.set(definition.id, definition);
        hotbar.renderQuickAccess();
        hotbar.select(hotbar.selected);
        return definition;
      },
      createTexturedBlock: async (name, color, pixels, noise, collision) => {
        const definition = await hotbar.options.createTexturedBlock(
          name, color, pixels, noise, collision,
        );
        hotbar.libraryBlocks.set(definition.id, definition);
        return definition;
      },
      updateSolidBlock: async (id, patch) => {
        const definition = await hotbar.options.updateSolidBlock(id, patch);
        hotbar.libraryBlocks.set(definition.id, definition);
        hotbar.renderQuickAccess();
        hotbar.select(hotbar.selected);
        return definition;
      },
      updateTexturedBlock: async (
        id, name, color, pixels, collectionId, noise, collision, editViewId,
      ) => {
        const definition = await hotbar.options.updateTexturedBlock(
          id, name, color, pixels, collectionId, noise, collision, editViewId,
        );
        hotbar.libraryBlocks.set(definition.id, definition);
        hotbar.renderQuickAccess();
        hotbar.select(hotbar.selected);
        return definition;
      },
      deleteLibraryBlock: async (id) => {
        await hotbar.options.deleteLibraryBlock(id);
        hotbar.removeLocalLibrary(id);
        hotbar.renderQuickAccess();
        hotbar.select(hotbar.selected);
      },
      patchCustomBlock: async (id, patch) => {
        const definition = await hotbar.options.patchCustomBlock(id, patch);
        hotbar.customBlocks.set(definition.id, definition);
        hotbar.renderQuickAccess();
        hotbar.select(hotbar.selected);
        return definition;
      },
      updateCustomBlock: async (
        id, name, gridSize, voxels, noise, collision, editViewId,
      ) => {
        const definition = await hotbar.options.updateCustomBlock(
          id, name, gridSize, voxels, noise, collision, editViewId,
        );
        hotbar.customBlocks.set(definition.id, definition);
        hotbar.renderQuickAccess();
        hotbar.select(hotbar.selected);
        return definition;
      },
      deleteCustomBlock: async (id) => {
        await hotbar.options.deleteCustomBlock(id);
        hotbar.removeLocalCustom(id);
        hotbar.renderQuickAccess();
        hotbar.select(hotbar.selected);
      },
      createCustomBlockView: async (id, name, sourceViewId) => {
        const definition = await hotbar.options.createCustomBlockView(
          id, name, sourceViewId,
        );
        hotbar.customBlocks.set(definition.id, definition);
        hotbar.renderQuickAccess();
        hotbar.select(hotbar.selected);
        return definition;
      },
      activateCustomBlockView: async (id, viewId) => {
        const definition = await hotbar.options.activateCustomBlockView(id, viewId);
        hotbar.customBlocks.set(definition.id, definition);
        hotbar.renderQuickAccess();
        hotbar.select(hotbar.selected);
        return definition;
      },
      renameCustomBlockView: async (id, viewId, name) => {
        const definition = await hotbar.options.renameCustomBlockView(id, viewId, name);
        hotbar.customBlocks.set(definition.id, definition);
        return definition;
      },
      deleteCustomBlockView: async (id, viewId) => {
        const definition = await hotbar.options.deleteCustomBlockView(id, viewId);
        hotbar.customBlocks.set(definition.id, definition);
        hotbar.renderQuickAccess();
        hotbar.select(hotbar.selected);
        return definition;
      },
      createLibraryBlockView: async (id, name, sourceViewId) => {
        const definition = await hotbar.options.createLibraryBlockView(
          id, name, sourceViewId,
        );
        hotbar.libraryBlocks.set(definition.id, definition);
        hotbar.renderQuickAccess();
        hotbar.select(hotbar.selected);
        return definition;
      },
      activateLibraryBlockView: async (id, viewId) => {
        const definition = await hotbar.options.activateLibraryBlockView(id, viewId);
        hotbar.libraryBlocks.set(definition.id, definition);
        hotbar.renderQuickAccess();
        hotbar.select(hotbar.selected);
        return definition;
      },
      renameLibraryBlockView: async (id, viewId, name) => {
        const definition = await hotbar.options.renameLibraryBlockView(id, viewId, name);
        hotbar.libraryBlocks.set(definition.id, definition);
        return definition;
      },
      deleteLibraryBlockView: async (id, viewId) => {
        const definition = await hotbar.options.deleteLibraryBlockView(id, viewId);
        hotbar.libraryBlocks.set(definition.id, definition);
        hotbar.renderQuickAccess();
        hotbar.select(hotbar.selected);
        return definition;
      },
      updateCustomBlockAnimation: async (id, animation) => {
        const definition = await hotbar.options.updateCustomBlockAnimation(id, animation);
        hotbar.customBlocks.set(definition.id, definition);
        hotbar.renderQuickAccess();
        hotbar.select(hotbar.selected);
        return definition;
      },
      updateLibraryBlockAnimation: async (id, animation) => {
        const definition = await hotbar.options.updateLibraryBlockAnimation(id, animation);
        hotbar.libraryBlocks.set(definition.id, definition);
        hotbar.renderQuickAccess();
        hotbar.select(hotbar.selected);
        return definition;
      },
    };
  }

  /** Dropping a quick-access block anywhere outside the toolbar clears that slot. */
  private bindQuickSlotRemoveDrop(): void {
    const isBlockDrag = (transfer: DataTransfer | null): boolean => {
      if (!transfer) return false;
      const types = [...transfer.types];
      return types.includes('application/x-blankforge-block') || types.includes('text/plain');
    };
    document.addEventListener('dragover', (event) => {
      if (!isBlockDrag(event.dataTransfer)) return;
      if (this.quickRoot.contains(event.target as Node)) return;
      this.clearSlotDragOver();
      allowDrop(event);
    });
    document.addEventListener('drop', (event) => {
      this.clearSlotDragOver();
      if (this.quickRoot.contains(event.target as Node)) return;
      const payload = readDragPayload(event.dataTransfer);
      if (payload?.source !== 'quick' || payload.slot === undefined) return;
      event.preventDefault();
      this.types[payload.slot] = 0;
      // Defer rebuild so the drag source stays mounted until dragend (avoids stuck cursors).
      this.commitLoadoutDeferred(payload.slot);
    });
  }

  private bindDragVisuals(): void {
    document.addEventListener('dragend', () => {
      this.clearSlotDragOver();
      document.body.style.removeProperty('cursor');
    });
  }

  private setSlotDragOver(slot: HTMLElement): void {
    for (const entry of this.slots) {
      entry.classList.toggle('drag-over', entry === slot);
    }
  }

  private clearSlotDragOver(): void {
    for (const entry of this.slots) {
      entry.classList.remove('drag-over');
    }
  }

  private renderQuickAccess(): void {
    this.root.replaceChildren();
    this.slots.length = 0;
    this.types.forEach((type, index) => {
      const slot = document.createElement('div');
      slot.className = 'quick-slot' + (type === 0 ? ' empty' : '');
      slot.dataset.slot = String(index);
      slot.title = type === 0 ? 'Empty quick slot' : this.nameFor(type);
      slot.append(createBlockIcon(type, this.customBlocks, this.libraryBlocks));
      slot.draggable = type !== 0;
      slot.onclick = () => this.select(index);
      slot.ondragstart = (event) => {
        if (type === 0 || !event.dataTransfer) return;
        setDragPayload(event.dataTransfer, {
          source: 'quick',
          type,
          slot: index,
        });
        const icon = slot.querySelector<HTMLCanvasElement>('.block-icon');
        if (icon) setBlockDragImage(event, icon);
        slot.classList.add('dragging');
      };
      slot.ondragend = () => {
        slot.classList.remove('dragging');
        this.clearSlotDragOver();
        document.body.style.removeProperty('cursor');
      };
      slot.ondragover = (event) => {
        allowDrop(event);
        this.setSlotDragOver(slot);
      };
      slot.ondragleave = (event) => {
        const next = event.relatedTarget as Node | null;
        if (next && slot.contains(next)) return;
        slot.classList.remove('drag-over');
      };
      slot.ondrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.clearSlotDragOver();
        const payload = readDragPayload(event.dataTransfer);
        if (!payload) return;
        if (payload.source === 'quick' && payload.slot !== undefined) {
          const previous = this.types[index];
          this.types[index] = payload.type;
          this.types[payload.slot] = previous;
        } else {
          this.types[index] = payload.type;
        }
        this.commitLoadoutDeferred(index);
      };

      const key = document.createElement('span');
      key.className = 'key';
      key.textContent = String((index + 1) % 10);
      slot.appendChild(key);
      if (type === 0) {
        const empty = document.createElement('span');
        empty.className = 'empty-mark';
        empty.textContent = '+';
        slot.appendChild(empty);
      }
      this.root.appendChild(slot);
      this.slots.push(slot);
    });
  }

  private toggleInventory(): void {
    if (this.isInventoryOpen) {
      this.closeInventory(true);
      return;
    }
    this.inventory.render();
    this.inventoryRoot.classList.add('visible');
    this.quickRoot.classList.add('visible', 'editing');
    if (document.pointerLockElement) document.exitPointerLock();
  }

  private closeInventory(relock: boolean): void {
    this.inventoryRoot.classList.remove('visible');
    this.quickRoot.classList.remove('editing');
    this.quickRoot.classList.toggle('visible', this.gameVisible);
    if (relock) this.options.canvas.requestPointerLock();
  }

  private commitLoadout(selectedSlot: number): void {
    this.selected = selectedSlot;
    this.renderQuickAccess();
    this.select(selectedSlot);
    this.options.onChange([...this.types]);
  }

  /** Apply loadout after the current drag sequence finishes. */
  private commitLoadoutDeferred(selectedSlot: number): void {
    window.setTimeout(() => {
      this.commitLoadout(selectedSlot);
      document.body.style.removeProperty('cursor');
    }, 0);
  }

  private nameFor(type: number): string {
    const libraryId = libraryBlockId(type);
    if (libraryId !== null) {
      return this.libraryBlocks.get(libraryId)?.name ?? 'Library block';
    }
    const id = customBlockId(type);
    return id === null
      ? blockTypeById(type).name
      : this.customBlocks.get(id)?.name ?? 'Custom block';
  }

  private select(index: number): void {
    if (index < 0 || index >= this.slots.length) return;
    this.slots[this.selected]?.classList.remove('selected');
    this.selected = index;
    this.slots[index]?.classList.add('selected');
  }
}

function isEditableTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLElement &&
    (target.isContentEditable ||
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement);
}
